import React from 'react';
import { Modal, ShimmerElementType, Stack, useTheme } from '@fluentui/react';
import { Text } from 'common/components/Text';
import usePlatform from 'common/hooks/usePlatform';
import { IAppTheme } from 'common/theme/AppTheme';
import { pageDataService } from 'common/utilities';
import { screenReaderOnlyStyles } from 'common/utilities/Accessibility';
import { useLocalizedStrings } from 'i18n';

import { IconButton } from '../Buttons/v8/IconButton';
import { Shimmer } from '../Shimmer';

import { IDrawerProps } from './Drawer.Props';
import { getModalStyles, getStyles, useModalOverlayStyles } from './Drawer.styles';

export const Drawer = (props: IDrawerProps) => {
  const {
    position = 'bottom',
    children,
    onDismiss,
    shouldDismiss,
    animationLength = 200,
    hideHandle = false,
    isLoading,
    onRenderLoading,
    handleAriaLabel,
    title,
    styles,
  } = props;
  const { t } = useLocalizedStrings(); // using default namespace
  const isWebView = pageDataService.isWebView();
  const { isIOSDevice } = usePlatform();

  const [animationType, setAnimationType] = React.useState<'opening' | 'closing'>('closing');

  const animateDismiss = React.useCallback(() => {
    setAnimationType('closing');
    setTimeout(() => {
      onDismiss();
    }, animationLength);
  }, [animationLength, onDismiss]);

  const onTryDismiss = React.useCallback(async () => {
    if (shouldDismiss) {
      const dismissResult = await shouldDismiss();
      if (dismissResult) {
        animateDismiss();
      }
    } else {
      animateDismiss();
    }
  }, [animateDismiss, shouldDismiss]);

  const theme: IAppTheme = useTheme();
  const classNames = getStyles(theme, position, hideHandle, styles);
  const modalOverlayStyles = useModalOverlayStyles(isWebView);

  React.useEffect(() => {
    setTimeout(() => {
      setAnimationType('opening');
    }, 0);
  }, []);

  const horizontalPositioning = position === 'left' || position === 'right';

  const loadingComponent = React.useCallback(() => {
    return onRenderLoading ? (
      onRenderLoading()
    ) : (
      <>
        <span style={screenReaderOnlyStyles}>{t('Loading')}</span>
        <Stack tokens={{ childrenGap: 12 }} horizontalAlign="center">
          <Shimmer width={100} shimmerElements={[{ type: ShimmerElementType.line, height: 16 }]} />
          <Shimmer
            styles={{ root: { width: '100%', maxWidth: 500 } }}
            shimmerElements={[{ type: ShimmerElementType.line, height: 20 }]}
          />
        </Stack>
      </>
    );
  }, [onRenderLoading, t]);

  return (
    <Modal
      isOpen
      allowTouchBodyScroll
      aria-busy={isLoading}
      isBlocking={false}
      onDismiss={onTryDismiss}
      styles={getModalStyles(theme, position, animationLength, animationType, isIOSDevice)}
      overlay={{ styles: modalOverlayStyles }}
    >
      <Stack horizontal={horizontalPositioning} reversed={position === 'left' || position === 'top'} className={classNames.container}>
        {!hideHandle && (
          <Stack className={classNames.handleContainer} verticalAlign="center" horizontalAlign="center">
            <IconButton onClick={onTryDismiss} ariaLabel={handleAriaLabel ?? t('Close')}>
              <div className={classNames.handleIcon} />
            </IconButton>
          </Stack>
        )}
        <Stack grow className={classNames.body}>
          {isLoading && loadingComponent()}
          {!isLoading && (
            <Stack tokens={{ childrenGap: 16 }}>
              {title && (
                <Stack.Item align="center">
                  <Text variant="Body1Strong">{title}</Text>
                </Stack.Item>
              )}
              <Stack.Item>{children}</Stack.Item>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};
